import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivateFn, Router } from '@angular/router';
import { AuthStore } from '@app/feature/authentication/store/auth.store';
import { AuthenticationService } from '@app/feature/authentication/service/authentication.service';
import {
  isPendingProfessionalRole,
  resolveHomeRoute,
} from '@core/utils/auth.util';
import { safeGet, safeJsonParse } from '@core/utils/safe-storage.util';
import { catchError, map, of } from 'rxjs';

/**
 * Garde du site public (/restaurants).
 *
 * - Visiteur non authentifié (pas de token valide) → accès autorisé (catalogue public).
 * - Client authentifié (CUSTOMER) ou rôle absent/inconnu → accès autorisé.
 * - Gérant / staff / admin / livreur / en attente → redirection vers son home
 *   (ex: /management/restaurants/dashboard, /admin/dashboard, /courier/dashboard, /management/pending).
 *
 * Le catalogue reste public côté backend (SecurityConfig permitAll) ; ce guard
 * évite seulement qu'un utilisateur professionnel connecté navigue dans l'UI client
 * par erreur (UX + séparation des contextes).
 */
export const CustomerGuard: CanActivateFn = () => {
  const router = inject(Router);
  const authStore = inject(AuthStore);
  const authService = inject(AuthenticationService);
  const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  const resolveRole = (): string | null => {
    // Le claim JWT (signé serveur) prime sur le store et le stockage local.
    const claim = authService.getRole();
    if (claim) return claim;
    const user = authStore.user();
    const raw = safeGet('user');
    const storedUser = safeJsonParse<{ role?: string | null } | null>(
      raw,
      null,
    );
    const userData = user ?? storedUser;
    return userData?.role ?? null;
  };

  const decide = (): boolean => {
    try {
      const role = resolveRole();

      if (!role || role === 'CUSTOMER') {
        return true;
      }

      if (isPendingProfessionalRole(role)) {
        if (isBrowser) router.navigate(['/management/pending']);
        return false;
      }

      const target = resolveHomeRoute(role);
      if (target === '/restaurants' || target === '/restaurants/home') {
        return true;
      }

      if (isBrowser) router.navigate([target]);
      return false;
    } catch {
      // Catalogue public par design : en cas de doute, laisser passer.
      return true;
    }
  };

  if (!isBrowser) {
    // Rendu serveur : la page publique doit toujours se prérendre ;
    // le client réévalue le guard à l'hydratation.
    return true;
  }

  if (authService.isTokenValid()) {
    return decide();
  }

  if (!authService.hasSessionHint()) {
    return true;
  }

  // Session potentiellement restaurable (cookie HttpOnly) : vérifier le rôle
  // réel avant de laisser un professionnel dans l'UI client.
  return authService.refreshToken().pipe(
    map((response) => {
      if (!authService.isTokenValid(response.accessToken) || !response.user) {
        authService.clearLocalSession();
        return true;
      }
      return decide();
    }),
    catchError(() => {
      authService.clearLocalSession();
      return of(true);
    }),
  );
};
